
import React, { useEffect } from 'react';
import type { ParkingReservation } from '../../types';
import ParkAndFlyReport from './ParkAndFlyReport';

export interface BulkParkingPrintData {
    reservations: ParkingReservation[];
    propertyName: string;
    selectedDate: string;
}

interface BulkParkingPrintPageProps extends BulkParkingPrintData {
    onDone: () => void;
}

const BulkParkingPrintPage: React.FC<BulkParkingPrintPageProps> = ({ reservations, propertyName, selectedDate, onDone }) => {
    useEffect(() => {
        const originalBodyStyle = document.body.style.cssText;
        document.body.style.cssText = 'background-color: white;';
        
        const timer = setTimeout(() => {
            window.print();
            onDone();
        }, 1500); // Longer delay for bulk print to ensure all forms render

        return () => {
            clearTimeout(timer);
            document.body.style.cssText = originalBodyStyle;
        };
    }, []);

    const ParkingForm = ({ reservation, label }: { reservation: ParkingReservation, label: string }) => (
        <div className="h-full border-2 border-black p-4 flex flex-col bg-white overflow-hidden box-border">
            {/* Header */}
            <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                    <div className="w-12 h-12 bg-[#006837] rounded-lg flex flex-col items-center justify-center p-1">
                        <div className="relative w-6 h-6 flex items-center justify-center">
                            <div className="w-5 h-5 border-[3px] border-[#FDB913] rounded-full"></div>
                            <div className="absolute bottom-0.5 right-0.5 w-3 h-[3px] bg-[#FDB913] rotate-45 rounded-full"></div>
                        </div>
                        <div className="text-white font-serif text-[7px] leading-none mt-0.5 font-bold">Quality</div>
                        <div className="text-white font-sans text-[4px] tracking-[0.1em] leading-none mt-0.5 font-bold uppercase">Hotel</div>
                    </div>
                    <div>
                        <h1 className="text-[10pt] font-black uppercase leading-tight">Quality Hotel Dorval</h1>
                        <p className="text-[7pt] text-gray-600 font-bold">1-800-361-6243</p>
                    </div>
                </div>
                {/* Removed Guest Copy label from top right */}
            </div>

            {/* Label Banner & Centered Title for Permit */}
            <div className="text-center mb-4">
                {label === "Parking Permit" && (
                    <div className="text-[14pt] font-black uppercase text-indigo-900 leading-tight mb-4">
                        Parking Permit<br />
                        Permis de Stationnement
                    </div>
                )}
                <div className={`inline-block px-4 py-1 rounded text-[8pt] font-black uppercase tracking-wider ${
                    label === "Parking Permit" ? "bg-black text-white" : "bg-gray-200 text-black border border-black"
                }`}>
                    {label === "Parking Permit" ? (
                        <div className="leading-tight">
                            Placer sur le tableau de bord<br />
                            Place on Dashboard
                        </div>
                    ) : "Guest Copy / Reçu Client"}
                </div>
            </div>

            {/* Main Data Grid */}
            {label === "Parking Permit" ? (
                <div className="flex flex-col border-2 border-black min-h-0 h-[180px]">
                    {/* Row 1: Dates */}
                    <div className="flex border-b border-black h-1/3">
                        <div className="flex-1 border-r border-black p-1 flex flex-col justify-center items-center bg-gray-50">
                            <span className="text-[6pt] font-black uppercase text-gray-500">Start Date</span>
                            <span className="text-[14pt] font-black leading-none mt-1">{reservation.startDate}</span>
                        </div>
                        <div className="flex-1 p-1 flex flex-col justify-center items-center bg-gray-50">
                            <span className="text-[6pt] font-black uppercase text-gray-500">Return Date</span>
                            <span className="text-[14pt] font-black leading-none mt-1">{reservation.endDate}</span>
                        </div>
                    </div>
                    {/* Row 2: Confirmation */}
                    <div className="border-b border-black h-1/3 p-1 flex flex-col justify-center items-center">
                        <span className="text-[6pt] font-black uppercase text-gray-500">Confirmation #</span>
                        <span className="text-[14pt] font-black leading-none mt-1">{reservation.confirmationNumber}</span>
                    </div>
                    {/* Row 3: Authorized */}
                    <div className="h-1/3 p-1 flex flex-col justify-center items-center">
                        <span className="text-[6pt] font-black uppercase text-gray-500">Authorized</span>
                        <span className="text-[14pt] font-black leading-none mt-1 truncate w-full text-center">
                            {reservation.authorizedBy === 'System' ? 'Reception' : reservation.authorizedBy}
                        </span>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-2 border-2 border-black h-[80px]">
                    <div className="border-r border-b border-black p-1 flex flex-col justify-center items-center bg-gray-50">
                        <span className="text-[5pt] font-black uppercase text-gray-500">Start Date</span>
                        <span className="text-[12pt] font-black leading-none mt-0.5">{reservation.startDate}</span>
                    </div>
                    <div className="border-b border-black p-1 flex flex-col justify-center items-center bg-gray-50">
                        <span className="text-[5pt] font-black uppercase text-gray-500">Return Date</span>
                        <span className="text-[12pt] font-black leading-none mt-0.5">{reservation.endDate}</span>
                    </div>
                    <div className="border-r border-black p-1 flex flex-col justify-center items-center">
                        <span className="text-[5pt] font-black uppercase text-gray-500">Confirmation #</span>
                        <span className="text-[10pt] font-black leading-none mt-0.5">{reservation.confirmationNumber}</span>
                    </div>
                    <div className="p-1 flex flex-col justify-center items-center">
                        <span className="text-[5pt] font-black uppercase text-gray-500">Authorized</span>
                        <span className="text-[10pt] font-black leading-none mt-0.5 truncate w-full text-center">
                            {reservation.authorizedBy === 'System' ? 'Reception' : reservation.authorizedBy}
                        </span>
                    </div>
                </div>
            )}

            {/* Guest Details */}
            <div className={`${label === "Parking Permit" ? "mt-4" : "mt-2"} grid grid-cols-2 gap-x-2 gap-y-1 text-[8pt]`}>
                <div className="border-b border-gray-200 pb-0.5">
                    <span className="text-[5pt] font-bold text-gray-400 uppercase block">Guest Name</span>
                    <span className="font-black truncate block text-[7pt]">{reservation.guestName}</span>
                </div>
                <div className="border-b border-gray-200 pb-0.5">
                    <span className="text-[5pt] font-bold text-gray-400 uppercase block">Vehicle Plate</span>
                    <span className="font-black block text-[7pt]">{reservation.vehicle_plate || '_______'}</span>
                </div>
                <div className="border-b border-gray-200 pb-0.5">
                    <span className="text-[5pt] font-bold text-gray-400 uppercase block">Parking Type</span>
                    <span className="font-black block text-[7pt]">{reservation.reservationSource}</span>
                </div>
                <div className="border-b border-gray-200 pb-0.5">
                    <span className="text-[5pt] font-bold text-gray-400 uppercase block">People</span>
                    <span className="font-black block text-[7pt]">{reservation.numberOfPeople || '1'}</span>
                </div>
                {reservation.customerEmail && (
                    <div className="col-span-2 border-b border-gray-200 pb-0.5">
                        <span className="text-[5pt] font-bold text-gray-400 uppercase block">Guest Email</span>
                        <span className="font-black block text-[7pt] truncate">{reservation.customerEmail}</span>
                    </div>
                )}
            </div>

            {/* Shuttle Instructions Box - Only for Guest Copy */}
            {label !== "Parking Permit" && (
                <div className="mt-auto pt-1">
                    <div className="bg-indigo-50 border-2 border-indigo-200 p-2 rounded-lg text-center">
                        <p className="text-[8pt] font-black uppercase text-indigo-900 mb-1 leading-tight border-b border-indigo-200 pb-1">
                            Shuttle Instructions / Instructions Navette
                        </p>
                        
                        <div className="flex flex-col items-center gap-1">
                            <p className="text-[13pt] font-black text-indigo-700">📞 438-523-6789</p>
                            
                            {/* French Block */}
                            <div className="space-y-0.5 text-indigo-900">
                                <p className="text-[8.5pt] font-bold leading-tight">
                                    Lorsque vous avez vos bagages en main à l'aéroport, appelez ce numéro.
                                </p>
                                <p className="text-[9pt] font-black leading-tight bg-yellow-200 px-2 rounded">
                                    Attendez à la porte 8 - 2e étage.
                                </p>
                                <p className="text-[8.5pt] font-black text-rose-700 uppercase leading-tight">
                                    Veuillez remettre ce coupon au chauffeur de la navette.
                                </p>
                            </div>

                            <div className="w-full border-t border-indigo-200 my-1"></div>

                            {/* English Block */}
                            <div className="space-y-0.5 text-indigo-900">
                                <p className="text-[8.5pt] font-bold leading-tight">
                                    When you have luggage in hand at the airport, call this number.
                                </p>
                                <p className="text-[9pt] font-black leading-tight bg-yellow-200 px-2 rounded">
                                    Wait at Door 8 - 2nd Floor.
                                </p>
                                <p className="text-[8.5pt] font-black text-rose-700 uppercase leading-tight">
                                    Please return this coupon to the shuttle driver.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    if (reservations.length === 0) {
        return (
            <div className="p-8 text-center bg-white min-h-screen">
                <h1 className="text-2xl font-bold">No reservations found for {selectedDate}</h1>
                <button onClick={onDone} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded">Back</button>
            </div>
        );
    }

    // Chunk reservations into pairs
    const chunkedReservations: ParkingReservation[][] = [];
    for (let i = 0; i < reservations.length; i += 2) {
        chunkedReservations.push(reservations.slice(i, i + 2));
    }

    return (
        <div className="bg-white min-h-screen text-black font-sans flex flex-col items-center">
            <style type="text/css" media="print">
            {`
                @page { size: A4 portrait; margin: 0; }
                body { -webkit-print-color-adjust: exact; color-adjust: exact; background-color: white; margin: 0; padding: 0; }
                .no-print { display: none !important; }
                .print-page { 
                    width: 210mm;
                    height: 297mm;
                    margin: 0 auto;
                    padding: 4mm;
                    page-break-after: always;
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    grid-template-rows: 1fr 1fr;
                    box-sizing: border-box;
                    background: white;
                }
                .print-page:last-child {
                    page-break-after: auto;
                }
                .form-container {
                    padding: 2mm;
                    box-sizing: border-box;
                    height: 100%;
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                }
                .border-separator-v { border-right: 1px dashed #ccc; }
                .border-separator-h { border-bottom: 1px dashed #ccc; }
                .report-page {
                    display: block;
                    width: 210mm;
                    height: 297mm;
                    padding: 1cm;
                    margin: 0 auto;
                    page-break-after: always;
                    box-sizing: border-box;
                    background: white;
                }
                .report-page:last-child {
                    page-break-after: auto;
                }
                .report-page > div {
                    width: 100%;
                    height: 100%;
                }
            `}
            </style>

            <div className="no-print fixed top-4 right-4 z-50">
                <button 
                    onClick={onDone}
                    className="bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Reservations
                </button>
            </div>
            
            {chunkedReservations.map((pair, pageIndex) => (
                <React.Fragment key={pageIndex}>
                    <div className="print-page">
                        {/* Reservation 1 */}
                        <div className="form-container border-separator-v border-separator-h">
                            <ParkingForm reservation={pair[0]} label="Parking Permit" />
                        </div>
                        <div className="form-container border-separator-h">
                            <ParkingForm reservation={pair[0]} label="Guest Copy" />
                        </div>

                        {/* Reservation 2 */}
                        {pair[1] ? (
                            <>
                                <div className="form-container border-separator-v">
                                    <ParkingForm reservation={pair[1]} label="Parking Permit" />
                                </div>
                                <div className="form-container">
                                    <ParkingForm reservation={pair[1]} label="Guest Copy" />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="form-container border-separator-v"></div>
                                <div className="form-container"></div>
                            </>
                        )}
                    </div>

                    {/* Park & Fly Reports for this pair */}
                    {pair.map(res => res.reservationSource === 'Park & Fly' && (
                        <div className="report-page" key={`report-${res.id}`}>
                            <ParkAndFlyReport reservation={res} />
                        </div>
                    ))}
                </React.Fragment>
            ))}
        </div>
    );
};

export default BulkParkingPrintPage;
